import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type { MicrosoftTeamsMessageEntity } from "@cohesive/api-entities";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

type MicrosoftTeamsMessagesResponse = ExecuteToolboxResponse<
  MicrosoftTeamsMessageEntity[]
>;

interface UseMicrosoftTeamsMessagesProps {
  connection: IntegrationConnection;
  chatId?: string;
  teamId?: string;
  channelId?: string;
  limit?: number;
  options?: Partial<UseQueryOptions<MicrosoftTeamsMessagesResponse, Error>>;
}

export function useMicrosoftTeamsMessages({
  connection,
  chatId,
  teamId,
  channelId,
  limit = 20,
  options = {},
}: UseMicrosoftTeamsMessagesProps) {
  return useQuery<MicrosoftTeamsMessagesResponse>({
    queryKey: [
      "microsoft-teams-messages",
      connection.id,
      chatId,
      teamId,
      channelId,
      limit,
    ],
    queryFn: async () => {
      const input: any = { limit };
      if (chatId) input.chat_id = chatId;
      if (teamId && channelId) {
        input.team_id = teamId;
        input.channel_id = channelId;
      }
      // Typed entities drive the rich message rows; their generic projections
      // ride along in data_generic and are what get placed on the canvas.
      return orgAPI.toolbox.execute<MicrosoftTeamsMessageEntity[], any>({
        integration_connection_id: connection.id,
        action_name: "microsoft-teams-list-messages",
        action_input: input,
        entity_format: "both",
      });
    },
    enabled:
      !!connection.integration_id && (!!chatId || (!!teamId && !!channelId)),
    staleTime: 1000 * 30,
    ...options,
  });
}
